import React, { useEffect, useRef } from 'react';
import './HowItWorks.css';

const STEPS = [
  { 
    step: '01', icon: '🏢', color: 'blue', side: 'left', 
    title: 'Admin Registration & Team Onboarding',  
    desc: 'A user registers as an admin and creates the organization workspace. The admin can then onboard recruiters by adding them to the system, enabling a structured hiring process.' 
  },
  { 
    step: '02', icon: '🔍', color: 'green', side: 'right', 
    title: 'Smart Profile Extraction',   
    desc: 'Our AI reads your JDs like an expert recruiter, instantly identifying the must-have skills, required certifications, and years of experience needed for success.' 
  },
  { 
    step: '03', icon: '🎯', color: 'blue', side: 'left', 
    title: 'Precision Candidate Ranking',      
    desc: 'Instead of basic keyword matching, MeruBot scores candidates based on how well they actually fit your criteria, instantly highlighting your "Top Picks" for the role.' 
  },
  { 
    step: '04', icon: '⚡', color: 'green', side: 'right', 
    title: 'Adaptive Interview Execution',  
    desc: 'Candidates enter either a Static path or a Dynamic AI path. In Dynamic mode, the AI generates follow-up questions in real-time based on the candidate’s answers.' 
  },
  { 
    step: '05', icon: '🧠', color: 'blue', side: 'left', 
    title: 'AI Answer Evaluation', 
    desc: 'Our engine evaluates every response for accuracy. In dynamic sessions, it automatically increases or maintains difficulty based on performance to find the true skill ceiling.' 
  },
  { 
    step: '06', icon: '🔎', color: 'green', side: 'right', 
    title: 'Comprehensive Talent Analytics', 
    desc: 'Generate deep-dive interview reports featuring overall scores and answer-by-answer analysis, ready for export and final decision-making.' 
  },
];

export default function HowItWorks() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
          } else {
            e.target.classList.remove('visible'); // 👈 allows repeat animation
          }
        }),
      { threshold: 0.15 }
    );

    const elements = ref.current?.querySelectorAll('.tl-item');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="how" className="section-wrap how-section" ref={ref}>
      <div className="centered">
        <div className="section-label">How It Works</div>
        <h2 className="section-title">From job role to results in minutes</h2>
        <p className="section-sub">
          MeruBot handles the entire screening pipeline end to end with zero manual effort from your team.
        </p>
      </div>

      <div className="tl-wrap">
        <div className="tl-line" />

        {STEPS.map((s, index) => (
          <div
            key={s.step}
            className={`tl-item tl-${s.side}`}
            style={{ transitionDelay: `${index * 120}ms` }} // 👈 stagger effect
          >
            <div className="tl-card">
              <div className={`tl-icon si-${s.color}`}>{s.icon}</div>
              <div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            </div>

            <div className="tl-node">
              <span className={`tl-step tl-step-${s.color}`}>
                {s.step}
              </span>
            </div>

            <div className="tl-spacer" />
          </div>
        ))}
      </div>
    </section>
  );
}